import { getReticulumFetchUrl, hubUrl } from "./utils/phoenix-utils";
import {
  updateEnvironmentForHub,
  getSceneUrlForHub,
  updateUIForHub,
  remountUI,
  updateRtcSfuConnectionState
} from "./hub";
import { loadLegacyRoomObjects } from "./utils/load-legacy-room-objects";
import { loadSavedEntityStates } from "./utils/entity-state-utils";
import qsTruthy from "./utils/qs_truthy";
import configs from "./utils/configs";
import configManager from "./singleton/ConfigManager";
import { resolveDialogEndpoint } from "./utils/dialog-endpoint-utils";
import {
  resolveRtcSfuDialogEndpoint,
  resolveRtcSfuOrchestratorUrl,
  shouldUseRtcSfuOrchestrator
} from "./utils/rtc-sfu-orchestrator-utils";
import { canEnterFromRoomSession } from "./utils/rtc-sfu-room-session";
import { resolveRoomResourceLimits } from "./utils/room-resource-limits-utils";
import { localClientID, pendingMessages, pendingParts } from "./bit-systems/networking";
import { storedUpdates } from "./bit-systems/network-receive-system";
import { eventRoomContext, getEndTime, getEventSession, getStartTime } from "./utils/events-utils";
import { isRoomHostOrAdmin, shouldRedirectTimedEventRoom } from "./utils/room-entry-access";
import AiAgents from "./singleton/AIAgents";
import { getRoomEntryAuthority, ROOM_ENTRY_AUTHORITY } from "./utils/room-entry-admission";

function unloadRoomObjects() {
  document.querySelectorAll("#objects-scene > [data-room-objects]").forEach(el => {
    // Invalidate an unfinished loader as well as its already-inflated objects.
    el.setAttribute("gltf-model-plus", { src: "" });
    el.parentNode.removeChild(el);
  });
  document.querySelectorAll("[pinnable]").forEach(el => {
    if (el.components.pinnable.data.pinned) {
      el.parentNode.removeChild(el);
    }
  });

  // Clean up empty object.gltf nodes
  document.querySelectorAll("#objects-scene .Room_Objects").forEach(el => {
    if (!el.children.length) {
      el.parentNode.parentNode.removeChild(el.parentNode);
    }
  });
}

function loadRoomObjects(hubId) {
  const objectsScene = document.querySelector("#objects-scene");
  const objectsUrl = getReticulumFetchUrl(`/${hubId}/objects.gltf`);
  const objectsEl = document.createElement("a-entity");
  objectsEl.setAttribute("data-room-objects", hubId);
  objectsEl.setAttribute("gltf-model-plus", { src: objectsUrl, useCache: false, inflate: true });
  objectsScene.appendChild(objectsEl);
}

function loadCurrentRoomObjects(hubId) {
  if (qsTruthy("newLoader")) {
    loadSavedEntityStates(APP.hubChannel);
    loadLegacyRoomObjects(hubId);
  } else {
    loadRoomObjects(hubId);
  }
}

function withResolvedRoomResourceLimits(roomSession, hub) {
  if (!roomSession) {
    return roomSession;
  }

  return {
    ...roomSession,
    roomResourceLimits: resolveRoomResourceLimits({
      subscriptionLimits: roomSession.limits,
      config: configManager.config,
      roomSettings: hub
    })
  };
}

async function resolveHubDialogEndpoint(
  hub,
  clientId,
  searchParams,
  { onStateChange = updateRtcSfuConnectionState, allocationGrant = null } = {}
) {
  const orchestratorUrl = resolveRtcSfuOrchestratorUrl({ searchParams, configUrl: configs.RTC_SFU_ORCHESTRATOR_URL });
  if (!shouldUseRtcSfuOrchestrator({ searchParams, orchestratorUrl })) {
    return resolveDialogEndpoint(hub, searchParams);
  }

  return resolveRtcSfuDialogEndpoint({
    hub,
    clientId,
    allocationGrant,
    searchParams,
    dashboardApiUrl: configs.CUSTOM_API_URL,
    orchestratorUrl,
    requestAllocationGrant: runtimeRoomId =>
      window.APP?.dashboardChannel?.platformRoomApi?.requestRuntimeAllocationGrant(runtimeRoomId),
    onStateChange
  });
}

async function requestActiveRtcSfuRoomSession({ roomId, clientId }) {
  const requestRoomSession = window.APP?.dashboardChannel?.requestRtcSfuRoomSession;

  if (!requestRoomSession) {
    throw new Error("RTC SFU room session endpoint is not available.");
  }

  const currentRoomSession = APP.rtcSfuRoomSession;
  const reconnectSession =
    currentRoomSession?.roomId === roomId || currentRoomSession?.hubId === roomId ? currentRoomSession : null;

  const roomSession = await requestRoomSession({
    roomId,
    clientId,
    reserve: true,
    roomSessionId: reconnectSession?.roomSessionId,
    reconnectToken: reconnectSession?.reconnectToken
  });

  if (!roomSession || !canEnterFromRoomSession(roomSession) || !roomSession.allocationGrant) {
    throw new Error("RTC SFU room session did not include an allocation grant.");
  }

  return roomSession;
}

export async function sendChangeHubMessage(hubId, addToHistory = true, waypoint = null, smoothTransition = false) {
  APP.hubChannel.sendMessage(
    {
      hubId,
      addToHistory,
      waypoint,
      smoothTransition
    },
    "change_hub"
  );
}

// A transition owns asynchronous room work until another navigation replaces it.
// Keep channel mutations serialized: migrateToHub leaves/rebinds the old channel
// internally, so a caller-side check alone cannot fence an in-flight migration.
function beginRoomTransition(hubId) {
  const previous = APP.roomTransition;
  if (previous?.pending) previous.controller.abort();
  const controller = new AbortController();
  const transition = {
    hubId,
    controller,
    pending: true,
    sourceTransition: previous?.pending ? previous.sourceTransition : previous,
    isCurrent: () => APP.roomTransition === transition && !controller.signal.aborted,
    isConnectionCurrent: () => APP.hub.hub_id === hubId && !controller.signal.aborted,
    check() {
      if (!this.isCurrent()) {
        const error = new Error("Room navigation was superseded.");
        error.name = "AbortError";
        throw error;
      }
    },
    async wait(promise) {
      this.check();
      let abort;
      const cancelled = new Promise((_, reject) => {
        abort = () => {
          const error = new Error("Room navigation was superseded.");
          error.name = "AbortError";
          reject(error);
        };
        controller.signal.addEventListener("abort", abort, { once: true });
      });
      try {
        const result = await Promise.race([promise, cancelled]);
        this.check();
        return result;
      } finally {
        controller.signal.removeEventListener("abort", abort);
      }
    }
  };
  APP.roomTransition = transition;
  // Stop a pending old media join/recovery before it can accept another callback.
  if (previous?.pending && APP.dialog._roomId === previous.hubId) APP.dialog.disconnect();
  return transition;
}

async function performHubChange(hubId, addToHistory, waypoint, smoothTransition, transition) {
  window.wasSmoothTransition = smoothTransition;
  AiAgents.clearRoomAgents();

  // Suppress on-screen join and leave messages until we receive a sync.
  APP.hideHubPresenceEvents = true;
  const scene = AFRAME.scenes[0];
  const hasEnteredRoom = scene.is("entered");
  scene.emit("hub-change-started", { hubId, addToHistory, waypoint, smoothTransition });

  if (hasEnteredRoom) {
    // Generate leave events for everyone in the room.
    Object.keys(APP.hubChannel.presence.state).forEach(key => {
      const clientId = APP.getSid(key);
      if (clientId !== localClientID) {
        pendingParts.push(clientId);
      }
    });
    // Reticulum "leaving" causes pinned objects to get cleaned up.
    pendingParts.push(APP.getSid("reticulum"));
  }

  let data;
  try {
    const migration = (APP.roomChannelMigration || Promise.resolve())
      .catch(() => {})
      .then(async () => {
        transition.check();
        const result = await APP.hubChannel.migrateToHub(hubId, { isCurrent: transition.isCurrent });
        transition.check();
        await APP.dashboardChannel.loadPlatformRuntimeHub(result.hubs[0], { syncSettings: false });
        transition.check();
        return result;
      });
    APP.roomChannelMigration = migration;
    data = await transition.wait(migration);
  } catch (e) {
    transition.check();
    const message = e.message.replace("Hub", "Verse");
    console.warn(`Failed to join ${hubId}: ${e.reason}|${message}`);
    APP.messageDispatch.log("joinFailed", { message });
    APP.roomTransition = transition.sourceTransition || null;
    transition.controller.abort();
    return;
  }

  const hub = data.hubs[0];
  transition.sourceTransition?.controller.abort();
  transition.sourceTransition = null;
  APP.hub = hub;

  if (APP.eventData) {
    const presentation = getEventSession(APP.eventData, eventRoomContext(hub.user_data).sessionSlug);
    const startTime = getStartTime(presentation || APP.eventData);
    const endTime = getEndTime(presentation || APP.eventData);
    const userCanBypassEventGate = isRoomHostOrAdmin({
      hubChannel: APP.hubChannel,
      isAdmin: typeof configs.isAdmin === "function" && configs.isAdmin()
    });

    if (shouldRedirectTimedEventRoom({ startTime, endTime, userCanBypassEventGate })) {
      return (window.location.href = hubUrl(hubId, {}, hub.slug, waypoint));
    }
  }

  if (addToHistory) {
    const search = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of search.entries()) {
      if (key !== "hub_id") {
        params[key] = value;
      }
    }
    window.history.pushState(null, null, hubUrl(hubId, params, hub.slug, waypoint));
  }

  const sceneUrl = await transition.wait(getSceneUrlForHub(hub));
  const shouldUpdateEnvironment =
    qsTruthy("newLoader") ||
    APP.environmentTransition?.ready === false ||
    document.querySelector("#environment-scene").childNodes[0].components["gltf-model-plus"].data.src !== sceneUrl;

  if (shouldUpdateEnvironment) {
    configManager.reset();
  }

  updateUIForHub(hub, APP.hubChannel);
  scene.emit("hub_updated", { hub });

  APP.subscriptions.setSubscribed(data.subscriptions.web_push);

  remountUI({
    hubIsBound: data.hub_requires_oauth,
    initialIsFavorited: data.subscriptions.favorites
  });

  if (hasEnteredRoom) {
    const remoteEntities = [];

    for (const id in NAF.entities.entities) {
      const owner = NAF.entities.entities[id].getAttribute("networked").owner;

      // Do not delete the entity with the media-image component and 360-equirectangular projection
      if (
        NAF.entities.entities[id] &&
        NAF.entities.entities[id].getAttribute("media-image") &&
        NAF.entities.entities[id].components["media-image"].data.projection === "360-equirectangular"
      ) {
        continue;
      }

      if (owner != NAF.clientId) {
        remoteEntities.push(id);
      }
    }

    remoteEntities.forEach(id => {
      NAF.entities.removeEntity(id);
    });

    // NAF.entities.removeRemoteEntities();
    await transition.wait(NAF.connection.adapter.disconnect());
    APP.dialog.disconnect();
    if (!qsTruthy("newLoader")) {
      unloadRoomObjects();
    }
    NAF.connection.connectedClients = {};
    NAF.connection.activeDataChannels = {};
    if (pendingMessages.length || storedUpdates.size) {
      pendingMessages.length = 0;
      storedUpdates.clear();
    }

    NAF.room = hub.hub_id;
  }

  let environmentConfigReady = Promise.resolve();

  if (shouldUpdateEnvironment) {
    const postFade = () => {
      scene.emit("reset_scene");
      environmentConfigReady = updateEnvironmentForHub(hub, APP.entryManager, smoothTransition, {
        isCurrent: transition.isCurrent,
        signal: transition.controller.signal
      });
      window.dispatchEvent(new CustomEvent("refresh-qr-code"));
    };

    const fader = document.getElementById("viewing-camera").components["fader"];

    if (smoothTransition) {
      postFade();
    } else {
      await transition.wait(fader.fadeOut());
      scene.emit("scene-loading", { name: hub.name });
      postFade();
    }
  } else {
    // A previous navigation may have completed the model but still owned the
    // delayed fade-in. The latest room owns revealing a reused environment.
    document.getElementById("viewing-camera").components.fader.fadeIn();
    // Room loading and scripting reset on hub-change-started. A reused model
    // will not emit model-loaded again, so restore its readiness explicitly.
    scene.emit(
      "environment-scene-loaded",
      document.querySelector("#environment-scene").firstElementChild?.getObject3D("mesh")
    );
    scene.emit("scene-loaded");
  }

  if (!hasEnteredRoom) {
    return;
  }

  await transition.wait(environmentConfigReady);

  if (getRoomEntryAuthority(configManager.config) === ROOM_ENTRY_AUTHORITY.RETICULUM) {
    APP.retChannel.push("change_hub", { hub_id: hub.hub_id });
    await transition.wait(NAF.connection.adapter.connect());
  } else {
    const qs = new URLSearchParams(location.search);
    let roomSession =
      APP.rtcSfuRoomSession?.roomId === hub.hub_id || APP.rtcSfuRoomSession?.hubId === hub.hub_id
        ? APP.rtcSfuRoomSession
        : null;
    let pendingRoomSessionRequest = null;
    const resolveRoomSession = async ({ isCurrent = () => true } = {}) => {
      const previousRoomSession = APP.rtcSfuRoomSession;
      const clientId = APP.dialog._clientId;
      let request = pendingRoomSessionRequest;
      if (!request || request.session !== previousRoomSession || request.clientId !== clientId) {
        request = {
          session: previousRoomSession,
          clientId,
          promise: requestActiveRtcSfuRoomSession({ roomId: hub.hub_id, clientId })
        };
        pendingRoomSessionRequest = request;
      }
      let nextRoomSession;
      try {
        nextRoomSession = await request.promise;
      } finally {
        if (pendingRoomSessionRequest === request) pendingRoomSessionRequest = null;
      }
      if (!transition.isConnectionCurrent() || !isCurrent() || APP.rtcSfuRoomSession !== previousRoomSession)
        return null;

      roomSession = withResolvedRoomResourceLimits(nextRoomSession, APP.hub || hub);
      APP.rtcSfuRoomSession = roomSession;

      return roomSession;
    };

    // A matching cached grant may already have been consumed by the previous
    // visit. Every new navigation obtains a fresh pending reservation.
    roomSession = await transition.wait(resolveRoomSession());
    // A newer room/session owns the connection. Do not continue this transition.
    if (!roomSession) return;
    APP.retChannel.push("change_hub", { hub_id: hub.hub_id });

    const dialogEndpoint = await transition.wait(
      resolveHubDialogEndpoint(hub, APP.dialog._clientId, qs, {
        allocationGrant: roomSession.allocationGrant,
        onStateChange: state => {
          if (transition.isCurrent()) updateRtcSfuConnectionState(state);
        }
      })
    );

    const mediaReady = APP.dialog.connect({
      isCurrent: transition.isConnectionCurrent,
      serverUrl: dialogEndpoint.serverUrl,
      roomId: hub.hub_id,
      serverParams: {
        host: dialogEndpoint.host,
        port: dialogEndpoint.port,
        turn: dialogEndpoint.turn,
        iceServers: dialogEndpoint.iceServers || dialogEndpoint.ice_servers,
        allocation: dialogEndpoint.allocation
      },
      scene,
      clientId: APP.dialog._clientId,
      forceTcp: APP.dialog._forceTcp,
      forceTurn: APP.dialog._forceTurn,
      iceTransportPolicy: APP.dialog._iceTransportPolicy,
      dialogEndpoint,
      resolveDialogEndpoint: async ({ onStateChange, isCurrent = () => true } = {}) => {
        const nextRoomSession = await resolveRoomSession({ isCurrent });
        if (!isCurrent() || !nextRoomSession) return null;

        return resolveHubDialogEndpoint(APP.hub || hub, APP.dialog._clientId, qs, {
          onStateChange,
          allocationGrant: nextRoomSession.allocationGrant
        });
      },
      resolveRoomSession,
      allocationGrant: roomSession.allocationGrant,
      peerMode: roomSession?.peerMode,
      roomSessionId: roomSession?.roomSessionId,
      reconnectToken: roomSession?.reconnectToken,
      roomResourceLimits: withResolvedRoomResourceLimits(roomSession, hub)?.roomResourceLimits,
      roomMediaQuality: roomSession?.roomMediaQuality
    });
    // Observe media errors immediately, but let data-room readiness own pinned
    // objects. An abandoned signaling promise must not hide objects.gltf.
    const mediaResult = mediaReady.then(
      () => null,
      error => error
    );
    await transition.wait(NAF.connection.adapter.connect());
    transition.check();
    loadCurrentRoomObjects(hubId);
    const mediaError = await transition.wait(mediaResult);
    if (mediaError) throw mediaError;
  }

  transition.check();
  if (getRoomEntryAuthority(configManager.config) === ROOM_ENTRY_AUTHORITY.RETICULUM) {
    loadCurrentRoomObjects(hubId);
  }

  APP.hubChannel.sendEnteredEvent();

  APP.messageDispatch.receive({
    type: "hub_changed",
    hubName: hub.name,
    showLineBreak: true,
    smoothTransition
  });
}
export async function changeHub(hubId, addToHistory = true, waypoint = null, smoothTransition = false) {
  const previous = APP.roomTransition;
  if (previous?.pending && previous.hubId === hubId) return previous.promise;
  if (hubId === APP.hub.hub_id && !previous?.pending) return;
  const transition = beginRoomTransition(hubId);
  transition.promise = performHubChange(hubId, addToHistory, waypoint, smoothTransition, transition)
    .catch(error => {
      if (transition.isCurrent()) throw error;
    })
    .finally(() => {
      transition.pending = false;
    });
  return transition.promise;
}

window.changeHub = changeHub;

// TODO see if there is a better way to do this with react router
window.addEventListener("popstate", function () {
  const qs = new URLSearchParams(location.search);
  const newHubId = qs.get("hub_id") || document.location.pathname.substring(1).split("/")[0];
  if (newHubId !== APP.hub.hub_id || (APP.roomTransition?.pending && APP.roomTransition.hubId !== newHubId)) {
    changeHub(newHubId, false, null, window.wasSmoothTransition || false).catch(error => {
      console.warn("Failed to restore room history", error);
      APP.messageDispatch.log("joinFailed", { message: error.message });
    });
  }
});
